
import './i18n';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import AuthGoogleCallback from "./pages/AuthGoogleCallback";
import ResetPassword from "./pages/ResetPassword";
import GoogleCallback from "./pages/GoogleCallback";
import Reset from "./pages/Reset";
import NotFound from "./pages/NotFound";
import { UnsupportedLanguageDialog } from "@/components/UnsupportedLanguageDialog";
import BackendErrorModal from "@/components/BackendErrorModal";
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useBackendStatus } from '@/hooks/useBackendStatus';
import { setGlobalRequestTracker } from '@/config/api';

const queryClient = new QueryClient();

const App = () => {
  const { i18n } = useTranslation();
  const [unsupportedLanguageDialogOpen, setUnsupportedLanguageDialogOpen] = useState(false);
  const { 
    isBackendError, 
    isSlowResponse, 
    showBackendErrorModal, 
    trackRequest, 
    dismissBackendErrorModal 
  } = useBackendStatus();

  // Set up global request tracking
  useEffect(() => {
    console.log('🔄 Setting up global request tracker in App.tsx');
    setGlobalRequestTracker(trackRequest);
  }, [trackRequest]);

  // Check for unsupported language variants
  useEffect(() => {
    const checkLanguageSupport = () => {
      // Define all supported language variants
      const supportedLanguageVariants = [
        'es', 'es-ES', 'es-MX', 'es-AR', 'es-CO', 'es-PE', 'es-VE', 'es-CL', 'es-EC', 'es-GT', 'es-CU', 'es-BO', 'es-DO', 'es-HN', 'es-PY', 'es-SV', 'es-NI', 'es-CR', 'es-PA', 'es-UY', 'es-PR',
        'en', 'en-US', 'en-GB', 'en-CA', 'en-AU', 'en-NZ', 'en-ZA', 'en-IE', 'en-IN', 'en-SG', 'en-PH', 'en-MY', 'en-HK',
        'pt', 'pt-BR', 'pt-PT', 'pt-AO', 'pt-MZ', 'pt-GW', 'pt-CV', 'pt-ST', 'pt-TL'
      ];
      
      // Get the primary language (first in the list) and current i18n language
      const primaryBrowserLanguage = navigator.languages?.[0] || navigator.language;
      const currentLanguage = i18n.language;
      
      console.log('🌐 Primary browser language:', primaryBrowserLanguage);
      console.log('🌐 Current i18n language:', currentLanguage);
      console.log('🌐 All browser languages:', navigator.languages);
      console.log('🌐 Supported language variants:', supportedLanguageVariants);
      
      // Check if the primary browser language OR current i18n language is supported
      const isPrimarySupported = supportedLanguageVariants.some(supportedLang => 
        primaryBrowserLanguage.toLowerCase() === supportedLang.toLowerCase()
      );
      
      const isCurrentSupported = supportedLanguageVariants.some(supportedLang => 
        currentLanguage.toLowerCase() === supportedLang.toLowerCase()
      );
      
      console.log('🌐 Is primary browser language supported?', isPrimarySupported);
      console.log('🌐 Is current i18n language supported?', isCurrentSupported);
      
      // Show modal if the current language is not supported
      const shouldShowModal = !isCurrentSupported;
      
      // TEMPORARY: Force show modal for testing by checking for a specific condition
      const urlParams = new URLSearchParams(window.location.search);
      const forceTestModal = urlParams.get('testlang') === 'true';
      
      console.log('🚨 Force test modal?', forceTestModal);
      console.log('🚨 Should show modal based on language?', shouldShowModal);
      console.log('🚨 Final decision - show modal?', shouldShowModal || forceTestModal);
      
      if (shouldShowModal || forceTestModal) {
        console.log('🚨 Showing unsupported language modal');
        setUnsupportedLanguageDialogOpen(true);
      } else {
        console.log('✅ Language is supported, modal will not show');
        setUnsupportedLanguageDialogOpen(false);
      }
    };

    // Add a small delay to ensure everything is initialized
    const timer = setTimeout(() => {
      console.log('🔄 Running language support check...');
      checkLanguageSupport();
    }, 100);
    
    // Listen for language changes
    const handleLanguageChange = (lng: string) => {
      console.log('🌐 Language changed to:', lng);
      const supportedLanguageVariants = [
        'es', 'es-ES', 'es-MX', 'es-AR', 'es-CO', 'es-PE', 'es-VE', 'es-CL', 'es-EC', 'es-GT', 'es-CU', 'es-BO', 'es-DO', 'es-HN', 'es-PY', 'es-SV', 'es-NI', 'es-CR', 'es-PA', 'es-UY', 'es-PR',
        'en', 'en-US', 'en-GB', 'en-CA', 'en-AU', 'en-NZ', 'en-ZA', 'en-IE', 'en-IN', 'en-SG', 'en-PH', 'en-MY', 'en-HK',
        'pt', 'pt-BR', 'pt-PT', 'pt-AO', 'pt-MZ', 'pt-GW', 'pt-CV', 'pt-ST', 'pt-TL'
      ];
      
      if (supportedLanguageVariants.some(supportedLang => 
        lng.toLowerCase() === supportedLang.toLowerCase()
      )) {
        console.log('✅ Language is now supported, closing modal');
        setUnsupportedLanguageDialogOpen(false);
      }
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      clearTimeout(timer);
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  console.log('🎭 App render - Modal open state:', unsupportedLanguageDialogOpen);
  console.log('🚨 Backend error modal state:', showBackendErrorModal);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          {/* Global Unsupported Language Dialog */}
          <UnsupportedLanguageDialog
            open={unsupportedLanguageDialogOpen}
            onOpenChange={setUnsupportedLanguageDialogOpen}
          />
          {/* Global Backend Error Modal */}
          <BackendErrorModal
            isOpen={showBackendErrorModal}
            onClose={dismissBackendErrorModal}
            isSlowResponse={isSlowResponse}
          />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth/google/callback" element={<AuthGoogleCallback />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/:analysisId" element={<Dashboard />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
